import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  useTheme,
  Skeleton,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  LinearProgress,
  Rating,
} from "@mui/material";
import { tokens } from "../../theme";
import {
  CalendarToday,
  EventAvailable,
  HourglassTop,
  ReceiptLong,
  People,
  TrendingUp,
  AccessTime,
  Refresh,
  EventNote,
  AttachMoney,
  Star,
  MailOutline,
  Warning,
  Groups,
  PersonOff,
  Extension,
} from "@mui/icons-material";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import StatBox from "../../utils/components/StatBox";
import DasboardHeader from "../header/DasboardHeader";
import {
  getReservationsSummary,
  getAppointmentsSummary,
  getInvoicesSummary,
  getUsersSummary,
  getReviewsSummary,
  getReceiptsSummary,
  getContactInquiriesSummary,
  buildMonthlyRevenueData,
  buildInvoiceStatusData,
  buildEventTypeData,
  buildUpcomingReservations,
  buildRecentActivity,
  buildPaymentMethodData,
  buildOverdueAgingData,
  buildVipStats,
  buildAppointmentTypeData,
  buildNoShowStats,
  buildCapacityStats,
  buildAddOnPopularity,
  buildRatingBars,
} from "./DashboardService";

// ─── Helpers ────────────────────────────────────────────────
const fmtCurrency = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);

const fmtDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const fmtTime = (d) => {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const statusColor = {
  Booked: "#10b981", Pending: "#f59e0b", Cancelled: "#ef4444",
  Done: "#6b7280", Completed: "#10b981", Paid: "#10b981",
  Due: "#f59e0b", Overdue: "#ef4444", "Due Immediately": "#f97316",
  "Partially Paid": "#8b5cf6", Withdrawn: "#6b7280",
  Scheduled: "#3b82f6", Requested: "#3b82f6", Confirmed: "#10b981",
};

const activityTypeColor = (type) => {
  if (type === "reservation") return "#10b981";
  if (type === "invoice") return "#8b5cf6";
  return "#3b82f6";
};

// ─── Reusable Mini Card ─────────────────────────────────────
const MiniCard = ({ icon, iconColor, value, label, loading: isLoading, cardSx }) => (
  <Box sx={{ ...cardSx, p: "20px" }}>
    <Box display="flex" alignItems="center" gap="12px" mb="8px">
      <Box sx={{ width: 40, height: 40, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(iconColor, 0.12) }}>
        {icon}
      </Box>
      {isLoading ? <Skeleton variant="text" width="50%" height={32} /> : (
        <Typography variant="h4" fontWeight="bold" sx={{ color: "inherit" }}>{value}</Typography>
      )}
    </Box>
    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>{label}</Typography>
  </Box>
);

// ─── Main Component ─────────────────────────────────────────
const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState({ booked: 0, pending: 0, cancelled: 0, done: 0, total: 0, all: [] });
  const [appointments, setAppointments] = useState({ total: 0, upcoming: 0, all: [] });
  const [invoices, setInvoices] = useState({ total: 0, invoices: [], statusCounts: {}, totalRevenue: 0, totalOutstanding: 0 });
  const [users, setUsers] = useState({ total: 0 });
  const [reviews, setReviews] = useState({ total: 0, average: 0, distribution: {}, recent: [] });
  const [receipts, setReceipts] = useState({ total: 0, receipts: [] });
  const [contacts, setContacts] = useState({ total: 0, unread: 0, statusCounts: {}, inquiries: [] });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [resSummary, apptSummary, invSummary, usersSummary, revSummary, recSummary, contSummary] =
        await Promise.all([
          getReservationsSummary(),
          getAppointmentsSummary(),
          getInvoicesSummary(),
          getUsersSummary(),
          getReviewsSummary(),
          getReceiptsSummary(),
          getContactInquiriesSummary(),
        ]);
      setReservations(resSummary);
      setAppointments(apptSummary);
      setInvoices(invSummary);
      setUsers(usersSummary);
      setReviews(revSummary);
      setReceipts(recSummary);
      setContacts(contSummary);
    } catch (e) {
      console.error("Dashboard fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Derived data
  const monthlyRevenue = buildMonthlyRevenueData(invoices.invoices);
  const invoiceStatusPie = buildInvoiceStatusData(invoices.statusCounts);
  const eventTypeBars = buildEventTypeData(reservations.all);
  const upcomingList = buildUpcomingReservations(reservations.all);
  const activityFeed = buildRecentActivity(reservations.all, invoices.invoices, appointments.all);
  const paymentMethodPie = buildPaymentMethodData(receipts.receipts);
  const overdueAging = buildOverdueAgingData(invoices.invoices);
  const vipStats = buildVipStats(reservations.all);
  const appointmentTypes = buildAppointmentTypeData(appointments.all);
  const noShowStats = buildNoShowStats(appointments.all);
  const capacityStats = buildCapacityStats(reservations.all);
  const addOnPop = buildAddOnPopularity(reservations.all);
  const ratingBars = buildRatingBars(reviews.distribution);

  // ─── Style helpers ──────────────────────────────────────────
  const cardSx = {
    backgroundColor: isDark ? colors.primary[400] : "#FFFFFF",
    borderRadius: "16px",
    border: `1px solid ${isDark ? colors.primary[300] : colors.grey[200]}`,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    overflow: "hidden",
  };

  const chartTheme = {
    axis: { ticks: { text: { fill: colors.grey[100], fontSize: 12 } }, legend: { text: { fill: colors.grey[100], fontSize: 13 } } },
    grid: { line: { stroke: isDark ? colors.primary[300] : colors.grey[200], strokeWidth: 1 } },
    legends: { text: { fill: colors.grey[100], fontSize: 12 } },
    tooltip: { container: { background: isDark ? colors.primary[400] : "#fff", color: colors.grey[100], borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" } },
  };

  const fmtAxisK = (v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`);

  return (
    <Box m="20px" sx={{ width: "97%", marginTop: "6rem" }}>
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="12px">
        <DasboardHeader title="DASHBOARD" subtitle="Welcome to your admin dashboard" />
        <Tooltip title="Refresh data">
          <IconButton onClick={fetchAll} disabled={loading} sx={{ backgroundColor: isDark ? colors.primary[400] : colors.grey[200], "&:hover": { backgroundColor: colors.greenAccent[500], color: "#fff" }, transition: "all 0.2s" }}>
            <Refresh sx={{ animation: loading ? "spin 1s linear infinite" : "none", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="20px">

        {/* ═══ ROW 1: KPI Cards (6 cards) ═══════════════════ */}
        {loading ? (
          <>{[1,2,3,4,5,6].map(i => <Box key={i} gridColumn="span 2" sx={cardSx} p="24px"><Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: "10px", mb: 1 }} /><Skeleton variant="text" width="60%" height={32} /><Skeleton variant="text" width="80%" height={16} /></Box>)}</>
        ) : (
          <>
            <Box gridColumn="span 2"><StatBox title={reservations.booked?.toString() || "0"} subtitle="Active Bookings" increase={`${reservations.total} total`} iconBg="#10b981" icon={<EventAvailable sx={{ color: "white", fontSize: 22 }} />} /></Box>
            <Box gridColumn="span 2"><StatBox title={reservations.pending?.toString() || "0"} subtitle="Pending Action" increase="Needs review" iconBg="#f59e0b" icon={<HourglassTop sx={{ color: "white", fontSize: 22 }} />} /></Box>
            <Box gridColumn="span 2"><StatBox title={appointments.upcoming?.toString() || "0"} subtitle="Upcoming Appts" increase={`${appointments.total} total`} iconBg="#3b82f6" icon={<CalendarToday sx={{ color: "white", fontSize: 22 }} />} /></Box>
            <Box gridColumn="span 2"><StatBox title={fmtCurrency(invoices.totalOutstanding)} subtitle="Outstanding" increase={`${invoices.total} invoices`} iconBg="#ef4444" icon={<ReceiptLong sx={{ color: "white", fontSize: 22 }} />} /></Box>
            <Box gridColumn="span 2"><StatBox title={reviews.average ? `${reviews.average} ★` : "—"} subtitle="Avg Rating" increase={`${reviews.total} reviews`} iconBg="#f59e0b" icon={<Star sx={{ color: "white", fontSize: 22 }} />} /></Box>
            <Box gridColumn="span 2"><StatBox title={contacts.unread?.toString() || "0"} subtitle="Unread Inquiries" increase={`${contacts.total} total`} iconBg={contacts.unread > 0 ? "#ef4444" : "#6b7280"} icon={<MailOutline sx={{ color: "white", fontSize: 22 }} />} /></Box>
          </>
        )}

        {/* ═══ ROW 2: Revenue Chart + Invoice Pie ═══════════ */}
        <Box gridColumn="span 8" gridRow="span 2" sx={{ ...cardSx, minHeight: 360 }}>
          <Box p="20px 24px 0">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500, mb: "2px" }}>Revenue Overview</Typography>
                <Typography variant="h4" fontWeight="bold" sx={{ color: colors.grey[100] }}>{fmtCurrency(invoices.totalRevenue)}</Typography>
              </Box>
              <Chip icon={<TrendingUp sx={{ fontSize: 16 }} />} label="Last 6 Months" size="small" sx={{ bgcolor: alpha("#10b981", 0.1), color: "#10b981", fontWeight: 600, "& .MuiChip-icon": { color: "#10b981" } }} />
            </Box>
          </Box>
          <Box height="280px" px="12px">
            {loading ? <Box display="flex" justifyContent="center" alignItems="center" height="100%"><Skeleton variant="rectangular" width="90%" height="200px" sx={{ borderRadius: "8px" }} /></Box> : (
              <ResponsiveBar data={monthlyRevenue} keys={["revenue"]} indexBy="month" margin={{ top: 20, right: 20, bottom: 40, left: 60 }} padding={0.4} colors={[colors.greenAccent[500]]} borderRadius={6} theme={chartTheme} axisBottom={{ tickSize: 0, tickPadding: 12 }} axisLeft={{ tickSize: 0, tickPadding: 12, format: fmtAxisK }} enableGridX={false} enableLabel={false} tooltip={({ value, indexValue }) => (<Box sx={{ ...chartTheme.tooltip.container, p: "8px 12px" }}><strong>{indexValue}</strong>: {fmtCurrency(value)}</Box>)} />
            )}
          </Box>
        </Box>

        <Box gridColumn="span 4" gridRow="span 2" sx={{ ...cardSx, minHeight: 360 }}>
          <Box p="20px 24px 12px">
            <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500, mb: "2px" }}>Invoice Status</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: colors.grey[100] }}>{invoices.total} Total Invoices</Typography>
          </Box>
          <Box height="260px">
            {loading ? <Box display="flex" justifyContent="center" alignItems="center" height="100%"><Skeleton variant="circular" width={180} height={180} /></Box>
              : invoiceStatusPie.length > 0 ? (
                <ResponsivePie data={invoiceStatusPie} margin={{ top: 10, right: 80, bottom: 30, left: 80 }} innerRadius={0.6} padAngle={2} cornerRadius={4} colors={{ datum: "data.color" }} borderWidth={0} arcLinkLabelsTextColor={colors.grey[100]} arcLinkLabelsColor={{ from: "color" }} arcLinkLabelsSkipAngle={15} arcLabelsSkipAngle={10} arcLabelsTextColor="#fff" theme={chartTheme} tooltip={({ datum }) => (<Box sx={{ ...chartTheme.tooltip.container, p: "8px 12px" }}><strong>{datum.label}</strong>: {datum.value}</Box>)} />
              ) : <Box display="flex" justifyContent="center" alignItems="center" height="100%"><Typography variant="body2" color={colors.grey[500]}>No invoice data</Typography></Box>
            }
          </Box>
        </Box>

        {/* ═══ ROW 3: Upcoming + Activity Feed ══════════════ */}
        <Box gridColumn="span 5" sx={{ ...cardSx, p: 0 }}>
          <Box p="20px 24px 12px" borderBottom={`1px solid ${isDark ? colors.primary[300] : colors.grey[200]}`}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500, mb: "2px" }}>Upcoming This Week</Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: colors.grey[100] }}>{upcomingList.length} Events</Typography>
              </Box>
              <EventNote sx={{ color: colors.greenAccent[500], fontSize: 28 }} />
            </Box>
          </Box>
          <Box sx={{ maxHeight: 320, overflow: "auto" }}>
            {loading ? [0,1,2].map(i => <Box key={`skel-up-${i}`} p="16px 24px" borderBottom={`1px solid ${isDark ? colors.primary[300] : colors.grey[200]}`}><Skeleton variant="text" width="70%" height={22} /><Skeleton variant="text" width="50%" height={18} /></Box>)
              : upcomingList.length > 0 ? upcomingList.map((r) => (
                <Box key={r.id} p="14px 24px" display="flex" justifyContent="space-between" alignItems="center" borderBottom={`1px solid ${isDark ? colors.primary[300] : colors.grey[200]}`} sx={{ "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" }, transition: "background 0.15s" }}>
                  <Box>
                    <Typography variant="body1" fontWeight={600} sx={{ color: colors.grey[100] }}>{r.eventType}</Typography>
                    <Box display="flex" alignItems="center" gap="6px" mt="4px"><AccessTime sx={{ fontSize: 14, color: colors.grey[500] }} /><Typography variant="caption" sx={{ color: colors.grey[500] }}>{fmtDate(r.startingDateTime)} · {fmtTime(r.startingDateTime)}</Typography></Box>
                  </Box>
                  <Box textAlign="right">
                    <Chip label={r.status} size="small" sx={{ bgcolor: alpha(statusColor[r.status] || "#6b7280", 0.12), color: statusColor[r.status] || "#6b7280", fontWeight: 600, fontSize: "11px" }} />
                    <Typography variant="caption" display="block" mt="4px" sx={{ color: colors.grey[500] }}>{r.numberOfSeats} guests</Typography>
                  </Box>
                </Box>
              )) : <Box p="32px" textAlign="center"><Typography variant="body2" color={colors.grey[500]}>No upcoming events this week</Typography></Box>
            }
          </Box>
        </Box>

        <Box gridColumn="span 7" sx={{ ...cardSx, p: 0 }}>
          <Box p="20px 24px 12px" borderBottom={`1px solid ${isDark ? colors.primary[300] : colors.grey[200]}`}>
            <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500, mb: "2px" }}>Recent Activity</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: colors.grey[100] }}>Latest updates across the system</Typography>
          </Box>
          <Box sx={{ maxHeight: 320, overflow: "auto" }}>
            {loading ? [0,1,2,3].map(i => <Box key={`skel-act-${i}`} p="14px 24px" display="flex" gap="12px" borderBottom={`1px solid ${isDark ? colors.primary[300] : colors.grey[200]}`}><Skeleton variant="circular" width={36} height={36} /><Box flex={1}><Skeleton variant="text" width="60%" height={20} /><Skeleton variant="text" width="40%" height={16} /></Box></Box>)
              : activityFeed.length > 0 ? activityFeed.map((item) => (
                <Box key={item.id} p="12px 24px" display="flex" alignItems="center" gap="14px" borderBottom={`1px solid ${isDark ? colors.primary[300] : colors.grey[200]}`} sx={{ "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" }, transition: "background 0.15s" }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(activityTypeColor(item.type), 0.12) }}>
                    {item.type === "reservation" && <EventAvailable sx={{ fontSize: 18, color: "#10b981" }} />}
                    {item.type === "invoice" && <ReceiptLong sx={{ fontSize: 18, color: "#8b5cf6" }} />}
                    {item.type === "appointment" && <CalendarToday sx={{ fontSize: 18, color: "#3b82f6" }} />}
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={600} sx={{ color: colors.grey[100] }} noWrap>{item.title}</Typography>
                    <Typography variant="caption" sx={{ color: colors.grey[500] }} noWrap>{item.subtitle}</Typography>
                  </Box>
                  <Box textAlign="right" flexShrink={0}>
                    {item.amount != null && <Typography variant="body2" fontWeight={700} sx={{ color: colors.grey[100] }}>{fmtCurrency(item.amount)}</Typography>}
                    <Typography variant="caption" sx={{ color: colors.grey[500] }}>{fmtDate(item.date)}</Typography>
                  </Box>
                  <Chip label={item.status} size="small" sx={{ bgcolor: alpha(statusColor[item.status] || "#6b7280", 0.12), color: statusColor[item.status] || "#6b7280", fontWeight: 600, fontSize: "11px", minWidth: 80 }} />
                </Box>
              )) : <Box p="32px" textAlign="center"><Typography variant="body2" color={colors.grey[500]}>No recent activity</Typography></Box>
            }
          </Box>
        </Box>

        {/* ═══ ROW 4: Customer Reviews + Payment Methods ════ */}
        <Box gridColumn="span 5" sx={{ ...cardSx, p: "20px 24px" }}>
          <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500, mb: "2px" }}>Customer Reviews</Typography>
          <Box display="flex" alignItems="center" gap="12px" mb="16px">
            <Typography variant="h3" fontWeight="bold" sx={{ color: colors.grey[100] }}>{loading ? "—" : reviews.average || "—"}</Typography>
            {!loading && reviews.average > 0 && <Rating value={reviews.average} precision={0.1} readOnly size="medium" />}
            <Typography variant="body2" sx={{ color: colors.grey[500] }}>({reviews.total} reviews)</Typography>
          </Box>
          <Box height="160px">
            {loading ? <Skeleton variant="rectangular" width="100%" height="140px" sx={{ borderRadius: "8px" }} />
              : ratingBars.some(b => b.count > 0) ? (
                <ResponsiveBar data={ratingBars} keys={["count"]} indexBy="star" layout="horizontal" margin={{ top: 0, right: 20, bottom: 20, left: 40 }} padding={0.3} colors={["#f59e0b"]} borderRadius={4} theme={chartTheme} axisLeft={{ tickSize: 0, tickPadding: 8 }} axisBottom={{ tickSize: 0, tickPadding: 4 }} enableGridY={false} enableLabel labelTextColor="#fff" tooltip={({ value, indexValue }) => (<Box sx={{ ...chartTheme.tooltip.container, p: "8px 12px" }}><strong>{indexValue}</strong>: {value} review{value !== 1 ? "s" : ""}</Box>)} />
              ) : <Box display="flex" justifyContent="center" alignItems="center" height="100%"><Typography variant="body2" color={colors.grey[500]}>No reviews yet</Typography></Box>
            }
          </Box>
        </Box>

        <Box gridColumn="span 4" sx={{ ...cardSx, minHeight: 280 }}>
          <Box p="20px 24px 8px">
            <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500, mb: "2px" }}>Payment Methods</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: colors.grey[100] }}>{receipts.total} Receipts</Typography>
          </Box>
          <Box height="210px">
            {loading ? <Box display="flex" justifyContent="center" alignItems="center" height="100%"><Skeleton variant="circular" width={150} height={150} /></Box>
              : paymentMethodPie.length > 0 ? (
                <ResponsivePie data={paymentMethodPie} margin={{ top: 10, right: 70, bottom: 20, left: 70 }} innerRadius={0.55} padAngle={2} cornerRadius={4} colors={{ datum: "data.color" }} borderWidth={0} arcLinkLabelsTextColor={colors.grey[100]} arcLinkLabelsColor={{ from: "color" }} arcLinkLabelsSkipAngle={15} arcLabelsSkipAngle={10} arcLabelsTextColor="#fff" theme={chartTheme} tooltip={({ datum }) => (<Box sx={{ ...chartTheme.tooltip.container, p: "8px 12px" }}><strong>{datum.label}</strong>: {datum.value}</Box>)} />
              ) : <Box display="flex" justifyContent="center" alignItems="center" height="100%"><Typography variant="body2" color={colors.grey[500]}>No payment data</Typography></Box>
            }
          </Box>
        </Box>

        <Box gridColumn="span 3" display="flex" flexDirection="column" gap="20px">
          <MiniCard cardSx={cardSx} loading={loading} icon={<Warning sx={{ color: "#ef4444", fontSize: 22 }} />} iconColor="#ef4444" value={fmtCurrency(overdueAging.overdueTotal)} label="Total Overdue Amount" />
          <MiniCard cardSx={cardSx} loading={loading} icon={<Groups sx={{ color: "#8b5cf6", fontSize: 22 }} />} iconColor="#8b5cf6" value={`${vipStats.vipActive} VIP`} label={`${vipStats.vipCount} total VIP bookings`} />
          <MiniCard cardSx={cardSx} loading={loading} icon={<PersonOff sx={{ color: "#f97316", fontSize: 22 }} />} iconColor="#f97316" value={`${noShowStats.rate}%`} label={`${noShowStats.noShows} no-shows of ${noShowStats.total}`} />
        </Box>

        {/* ═══ ROW 5: Event Types + Overdue Aging + Stats ═══ */}
        <Box gridColumn="span 5" sx={{ ...cardSx, minHeight: 320 }}>
          <Box p="20px 24px 0">
            <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500, mb: "2px" }}>Event Type Distribution</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: colors.grey[100] }}>Most Popular Categories</Typography>
          </Box>
          <Box height="240px" px="12px">
            {loading ? <Box display="flex" justifyContent="center" alignItems="center" height="100%"><Skeleton variant="rectangular" width="90%" height="180px" sx={{ borderRadius: "8px" }} /></Box>
              : eventTypeBars.length > 0 ? (
                <ResponsiveBar data={eventTypeBars} keys={["count"]} indexBy="eventType" layout="horizontal" margin={{ top: 10, right: 20, bottom: 30, left: 130 }} padding={0.35} colors={["#8b5cf6"]} borderRadius={6} theme={chartTheme} axisBottom={{ tickSize: 0, tickPadding: 8 }} axisLeft={{ tickSize: 0, tickPadding: 12 }} enableGridY={false} enableLabel labelTextColor="#fff" tooltip={({ value, indexValue }) => (<Box sx={{ ...chartTheme.tooltip.container, p: "8px 12px" }}><strong>{indexValue}</strong>: {value}</Box>)} />
              ) : <Box display="flex" justifyContent="center" alignItems="center" height="100%"><Typography variant="body2" color={colors.grey[500]}>No reservation data</Typography></Box>
            }
          </Box>
        </Box>

        <Box gridColumn="span 4" sx={{ ...cardSx, minHeight: 320 }}>
          <Box p="20px 24px 0">
            <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500, mb: "2px" }}>Overdue Invoice Aging</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: colors.grey[100] }}>{fmtCurrency(overdueAging.overdueTotal)} Outstanding</Typography>
          </Box>
          <Box height="240px" px="12px">
            {loading ? <Box display="flex" justifyContent="center" alignItems="center" height="100%"><Skeleton variant="rectangular" width="90%" height="180px" sx={{ borderRadius: "8px" }} /></Box>
              : overdueAging.bars.length > 0 ? (
                <ResponsiveBar data={overdueAging.bars} keys={["count"]} indexBy="bucket" margin={{ top: 10, right: 20, bottom: 40, left: 90 }} padding={0.35} colors={({ data }) => data.color || "#ef4444"} borderRadius={6} theme={chartTheme} layout="horizontal" axisBottom={{ tickSize: 0, tickPadding: 8 }} axisLeft={{ tickSize: 0, tickPadding: 12 }} enableGridY={false} enableLabel labelTextColor="#fff" tooltip={({ value, indexValue }) => (<Box sx={{ ...chartTheme.tooltip.container, p: "8px 12px" }}><strong>{indexValue}</strong>: {value} invoice{value !== 1 ? "s" : ""}</Box>)} />
              ) : <Box display="flex" justifyContent="center" alignItems="center" height="100%"><Typography variant="body2" color="#10b981" fontWeight={600}>✓ No overdue invoices</Typography></Box>
            }
          </Box>
        </Box>

        <Box gridColumn="span 3" display="flex" flexDirection="column" gap="20px">
          <MiniCard cardSx={cardSx} loading={loading} icon={<People sx={{ color: "#3b82f6", fontSize: 22 }} />} iconColor="#3b82f6" value={users.total} label="Registered Users" />
          <MiniCard cardSx={cardSx} loading={loading} icon={<AttachMoney sx={{ color: "#10b981", fontSize: 22 }} />} iconColor="#10b981" value={fmtCurrency(invoices.totalRevenue)} label="Total Revenue Collected" />
          <MiniCard cardSx={cardSx} loading={loading} icon={<EventNote sx={{ color: "#f59e0b", fontSize: 22 }} />} iconColor="#f59e0b" value={reservations.done || 0} label="Completed Events" />
        </Box>

        {/* ═══ ROW 6: Capacity + Appointment Types + Add-Ons + Reschedules ═══ */}
        <Box gridColumn="span 3" sx={{ ...cardSx, p: "20px 24px" }}>
          <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500, mb: "4px" }}>Capacity Utilization</Typography>
          <Typography variant="h3" fontWeight="bold" sx={{ color: colors.grey[100], mb: "4px" }}>{loading ? "—" : `${capacityStats.utilization}%`}</Typography>
          <LinearProgress
            variant="determinate"
            value={loading ? 0 : capacityStats.utilization}
            sx={{ height: 10, borderRadius: 5, mb: "10px", bgcolor: isDark ? colors.primary[300] : colors.grey[200],
              "& .MuiLinearProgress-bar": { borderRadius: 5, bgcolor: capacityStats.utilization > 75 ? "#10b981" : capacityStats.utilization > 40 ? "#f59e0b" : "#ef4444" }
            }}
          />
          <Typography variant="caption" sx={{ color: colors.grey[500] }}>
            Avg {capacityStats.average} of 200 seats · {capacityStats.total} bookings
          </Typography>
        </Box>

        <Box gridColumn="span 3" sx={{ ...cardSx, p: "20px 24px" }}>
          <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500, mb: "8px" }}>Appointment Types</Typography>
          {loading ? <Skeleton variant="rectangular" height={120} sx={{ borderRadius: "8px" }} />
            : appointmentTypes.length > 0 ? appointmentTypes.slice(0, 5).map((at) => (
              <Box key={at.type} display="flex" justifyContent="space-between" alignItems="center" py="6px" borderBottom={`1px solid ${isDark ? colors.primary[300] : colors.grey[200]}`}>
                <Typography variant="body2" sx={{ color: colors.grey[100] }}>{at.type}</Typography>
                <Chip label={at.count} size="small" sx={{ bgcolor: alpha("#3b82f6", 0.12), color: "#3b82f6", fontWeight: 700, minWidth: 36 }} />
              </Box>
            )) : <Typography variant="body2" color={colors.grey[500]}>No data</Typography>
          }
        </Box>

        <Box gridColumn="span 3" sx={{ ...cardSx, p: "20px 24px" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb="8px">
            <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500 }}>Top Add-Ons</Typography>
            <Extension sx={{ color: colors.greenAccent[500], fontSize: 20 }} />
          </Box>
          {loading ? <Skeleton variant="rectangular" height={120} sx={{ borderRadius: "8px" }} />
            : addOnPop.length > 0 ? addOnPop.map((ao) => (
              <Box key={ao.name} display="flex" justifyContent="space-between" alignItems="center" py="6px" borderBottom={`1px solid ${isDark ? colors.primary[300] : colors.grey[200]}`}>
                <Typography variant="body2" sx={{ color: colors.grey[100] }} noWrap>{ao.name}</Typography>
                <Chip label={`×${ao.count}`} size="small" sx={{ bgcolor: alpha("#10b981", 0.12), color: "#10b981", fontWeight: 700 }} />
              </Box>
            )) : <Typography variant="body2" color={colors.grey[500]}>No add-on data</Typography>
          }
        </Box>

        <Box gridColumn="span 3" sx={{ ...cardSx, p: "20px 24px" }}>
          <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500, mb: "8px" }}>Reschedule Insights</Typography>
          <Box display="flex" alignItems="baseline" gap="6px" mb="12px">
            <Typography variant="h3" fontWeight="bold" sx={{ color: colors.grey[100] }}>{loading ? "—" : `${vipStats.rescheduleRate}%`}</Typography>
            <Typography variant="body2" sx={{ color: colors.grey[500] }}>reschedule rate</Typography>
          </Box>
          <Box display="flex" gap="12px">
            <Box sx={{ ...cardSx, p: "12px", flex: 1, textAlign: "center", border: "none", bgcolor: alpha("#f59e0b", 0.08) }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: "#f59e0b" }}>{vipStats.rescheduleCount}</Typography>
              <Typography variant="caption" sx={{ color: colors.grey[500] }}>Rescheduled</Typography>
            </Box>
            <Box sx={{ ...cardSx, p: "12px", flex: 1, textAlign: "center", border: "none", bgcolor: alpha("#ef4444", 0.08) }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: "#ef4444" }}>{reservations.cancelled || 0}</Typography>
              <Typography variant="caption" sx={{ color: colors.grey[500] }}>Cancelled</Typography>
            </Box>
          </Box>
        </Box>

      </Box>
    </Box>
  );
};

export default Dashboard;
